import { useMemo, useRef } from "react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { buildTimezoneOptions, timezoneSearchFilter } from "@/lib/timezones";

interface TimezoneSelectProps {
  id?: string;
  value?: string | null;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

export function TimezoneSelect({ id, value, onValueChange, disabled }: TimezoneSelectProps) {
  // Remember every non-canonical value this mount has displayed, not just the
  // current one — otherwise a stored alias (e.g. "Asia/Calcutta") disappears
  // from the list the moment the user clicks another zone to compare, with no
  // way back short of a reload.
  const seenValuesRef = useRef<string[]>([]);
  if (value && !seenValuesRef.current.includes(value)) {
    seenValuesRef.current = [...seenValuesRef.current, value];
  }
  const seenValues = seenValuesRef.current;

  const options = useMemo(() => {
    const base = buildTimezoneOptions(seenValues);
    if (!value) return base;
    // Pin the current selection to the top: cmdk keeps insertion order for
    // the empty search and for score ties, so without this the checked zone
    // sits buried at its alphabetical position (Europe/* is row ~216 of 316)
    // when the picker opens, and drifts around during weak searches.
    const idx = base.findIndex((o) => o.value === value);
    if (idx <= 0) return base;
    return [base[idx], ...base.slice(0, idx), ...base.slice(idx + 1)];
  }, [seenValues, value]);

  return (
    <SearchableSelect
      id={id}
      options={options}
      filter={timezoneSearchFilter}
      value={value ?? ""}
      onValueChange={(v) => {
        // SearchableSelect emits "" when the selected row is clicked again;
        // a timezone can't be cleared, so keep the current selection.
        if (v) onValueChange(v);
      }}
      placeholder="Select timezone"
      searchPlaceholder="Search timezones..."
      emptyText="No timezone found."
      disabled={disabled}
    />
  );
}
